import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { schulferienAPI } from '../lib/api';
import { DatePickerDE } from '../components/ui/date-picker-de';
import {
  Download,
  Plus,
  Trash2,
  Loader2,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Calendar,
  MapPin,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

const FERIEN_COLORS = {
  api: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  manual: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

export default function SchulferienManager() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [ferien, setFerien] = useState([]);
  const [bundeslaender, setBundeslaender] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualForm, setManualForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    bundesland: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [ferienRes, blRes] = await Promise.all([
        schulferienAPI.getAll(year),
        schulferienAPI.getBundeslaender(),
      ]);
      setFerien(ferienRes.data || []);
      setBundeslaender(blRes.data || []);
    } catch (error) {
      console.error('Failed to load Schulferien:', error);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFetch = async (bundesland = null) => {
    setFetching(true);
    try {
      const res = await schulferienAPI.fetch(year, bundesland);
      toast.success(`${res.data.added} Schulferien abgerufen für ${year}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Abrufen');
    } finally {
      setFetching(false);
    }
  };

  const handleAddManual = async () => {
    if (!manualForm.name || !manualForm.start_date || !manualForm.end_date || !manualForm.bundesland) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }
    setSaving(true);
    try {
      await schulferienAPI.createManual(manualForm);
      toast.success('Schulferien hinzugefügt');
      setAddDialogOpen(false);
      setManualForm({ name: '', start_date: '', end_date: '', bundesland: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" wirklich löschen?`)) return;
    try {
      await schulferienAPI.delete(id);
      toast.success('Eintrag gelöscht');
      loadData();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  // Group ferien by Bundesland
  const ferienByBL = {};
  ferien.forEach(f => {
    const bl = f.bundesland_name || f.bundesland;
    if (!ferienByBL[bl]) ferienByBL[bl] = [];
    ferienByBL[bl].push(f);
  });

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd.MM.yyyy', { locale: de });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="border-border/60" data-testid="schulferien-manager">
      <CardHeader>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <CardTitle className="font-heading flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-primary" />
              Schulferien
            </CardTitle>
            <CardDescription>
              Schulferien automatisch abrufen oder manuell eintragen
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setYear(y => y - 1)} data-testid="ferien-prev-year">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Badge variant="secondary" className="text-base px-3 py-1 font-semibold">
              {year}
            </Badge>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setYear(y => y + 1)} data-testid="ferien-next-year">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleFetch()}
            disabled={fetching}
            data-testid="fetch-ferien-btn"
          >
            {fetching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Automatisch abrufen
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAddDialogOpen(true)}
            data-testid="add-ferien-manual-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Manuell hinzufügen
          </Button>
        </div>

        {/* Info */}
        {ferien.length === 0 && !loading && (
          <div className="text-center py-8 text-muted-foreground">
            <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Keine Schulferien für {year} hinterlegt</p>
            <p className="text-xs mt-1">Klicken Sie "Automatisch abrufen" um Ferien für Ihre Niederlassungs-Bundesländer zu laden</p>
          </div>
        )}

        {/* Ferien List grouped by Bundesland */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          Object.entries(ferienByBL).sort(([a], [b]) => a.localeCompare(b)).map(([bl, entries]) => (
            <div key={bl} className="space-y-2" data-testid={`ferien-group-${bl}`}>
              <div className="flex items-center gap-2">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-sm font-semibold">{bl}</span>
                <Badge variant="secondary" className="text-xs">{entries.length}</Badge>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {entries.sort((a, b) => a.start_date.localeCompare(b.start_date)).map(entry => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-2.5 rounded-lg bg-secondary/30 border border-border/30 group"
                    data-testid={`ferien-entry-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">{entry.name}</span>
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${FERIEN_COLORS[entry.source]}`}>
                          {entry.source === 'api' ? 'API' : 'Manuell'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                        <Calendar className="h-3 w-3" />
                        {formatDate(entry.start_date)} - {formatDate(entry.end_date)}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                      onClick={() => handleDelete(entry.id, entry.name)}
                      data-testid={`delete-ferien-${entry.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {/* Add Manual Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading">Schulferien manuell eintragen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label>Bezeichnung</Label>
                <Input
                  value={manualForm.name}
                  onChange={(e) => setManualForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="z.B. Osterferien"
                  data-testid="manual-ferien-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Bundesland</Label>
                <Select
                  value={manualForm.bundesland}
                  onValueChange={(v) => setManualForm(prev => ({ ...prev, bundesland: v }))}
                >
                  <SelectTrigger data-testid="manual-ferien-bundesland">
                    <SelectValue placeholder="Bundesland wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {bundeslaender.map(bl => (
                      <SelectItem key={bl.code} value={bl.code}>{bl.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Startdatum</Label>
                  <DatePickerDE
                    value={manualForm.start_date}
                    onChange={(v) => setManualForm(prev => ({ ...prev, start_date: v }))}
                    placeholder="Startdatum"
                    testId="manual-ferien-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Enddatum</Label>
                  <DatePickerDE
                    value={manualForm.end_date}
                    onChange={(v) => setManualForm(prev => ({ ...prev, end_date: v }))}
                    placeholder="Enddatum"
                    testId="manual-ferien-end"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="rounded-full">
                  Abbrechen
                </Button>
                <Button onClick={handleAddManual} disabled={saving} className="rounded-full" data-testid="save-manual-ferien-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                  Hinzufügen
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
